import { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { resetDemoState } from '../services/demoData';

interface DemoContextType {
  isDemoMode: boolean;
  demoVersion: number; // Used to force re-renders when demo state changes
  toggleDemoMode: () => void;
  setDemoMode: (value: boolean) => void;
  resetDemo: () => void;
  refreshDemo: () => void; // Call this after modifying demo state
}

const DemoContext = createContext<DemoContextType | null>(null);

export const DemoProvider = ({ children }: { children: ReactNode }) => {
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [demoVersion, setDemoVersion] = useState(0);

  const refreshDemo = useCallback(() => {
    setDemoVersion(v => v + 1);
  }, []);

  const toggleDemoMode = useCallback(() => {
    setIsDemoMode(prev => {
      if (!prev) {
        // Entering demo mode - reset state
        resetDemoState();
        setDemoVersion(0);
      }
      return !prev;
    });
  }, []);

  const setDemoMode = useCallback((value: boolean) => {
    if (value && !isDemoMode) {
      // Entering demo mode - reset state
      resetDemoState();
      setDemoVersion(0);
    }
    setIsDemoMode(value);
  }, [isDemoMode]);

  const resetDemo = useCallback(() => {
    resetDemoState();
    setDemoVersion(v => v + 1);
  }, []);

  return (
    <DemoContext.Provider value={{
      isDemoMode,
      demoVersion,
      toggleDemoMode,
      setDemoMode,
      resetDemo,
      refreshDemo
    }}>
      {children}
    </DemoContext.Provider>
  );
};

export const useDemo = () => {
  const context = useContext(DemoContext);
  if (!context) {
    throw new Error('useDemo must be used within a DemoProvider');
  }
  return context;
};